<template>
  <div class="experts-page container">
    <div class="row mb-4 py-2">
      <div class="col-lg-9">
        <div class="row">
          <div class="col-xl-6 d-flex align-items-center">
            <div class="search-box d-flex align-items-center mb-4 mb-xl-0 w-100">
              <p class="text-uppercase text-secondary text-xs semi-bold m-0 pr-3">
                {{$t('feed.findAStory')}}
              </p>
              <div>
                <SearchForm :forNavbar="false"/>
              </div>
            </div>
          </div>
          <div class="col-xl-6">
            <div class="search-box d-flex align-items-center">
              <p class="text-uppercase text-secondary text-xs semi-bold m-0 pr-3">
                {{$t('feed.popularSearches')}}
              </p>
              <div class="popular-phrases">
                <template v-if="$i18n.locale === 'pl'">
                  <div v-for="(phrase, i) in popularPhrasesPL" :key="i" class="phrase">
                    <localized-link :to="{name: 'Search', params: {
                                    query: decodeURIComponent(phrase)
                                    }}">
                      <span class="text-xs">{{phrase}}</span>
                    </localized-link>
                  </div>
                </template>
                <template v-else-if="$i18n.locale === 'fr'">
                  <div v-for="(phrase, i) in popularPhrasesFR" :key="i" class="phrase">
                    <localized-link :to="{name: 'Search', params: {
                                    query: decodeURIComponent(phrase)
                                    }}">
                      <span class="text-xs">{{phrase}}</span>
                    </localized-link>
                  </div>
                </template>
                <template v-else-if="$i18n.locale === 'it'">
                  <div v-for="(phrase, i) in popularPhrasesIT" :key="i" class="phrase">
                    <localized-link :to="{name: 'Search', params: {
                                    query: decodeURIComponent(phrase)
                                    }}">
                      <span class="text-xs">{{phrase}}</span>
                    </localized-link>
                  </div>
                </template>
                <template v-else>
                  <div v-for="(phrase, i) in popularPhrasesEN" :key="i"
                       class="phrase"
                       :class="phrase.isHighlighted && 'highlighted'">
                    <localized-link :to="{name: 'Search', params: {
                                    query: decodeURIComponent(phrase.name)
                                    }}">
                      <span class="text-xs">{{phrase.name}}</span>
                    </localized-link>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-3">
        <localized-link :to="{name: 'Oil Gas Finder'}" class="oil-gas-finder-btn side-btn mt-4 mt-lg-0 text-uppercase">
          <img src="@/assets/icons/oil-gas-finder.svg"/>
          <span v-html="$t('feed.oilGasFinderBtn')"></span>
        </localized-link>

        <a href="https://catalogue.nissens.com/" target="_blank" class="catalogue-btn side-btn text-uppercase">
          <img src="@/assets/icons/cat.svg"/>
          <span v-html="$t('feed.catalogueBtn')"></span>
        </a>
      </div>
    </div>
    <div class="row mb-4">
      <div class="col-lg-9">
        <div v-if="featuredMaterials">
          <div class="bg-gradient-light-grey-vertical">
            <h2 class="text-xs text-secondary semi-bold font-italic-custom py-3 px-4 m-0">{{$t('feed.storiesRecommendedByExperts')}}</h2>
          </div>
          <div class="carousel">
            <VueSlickCarousel :arrows="true" :autoplaySpeed="3000">
              <div v-for="material in featuredMaterials" :key="material.id" class="carousel--item">
                <localized-link :to="{name: 'Material', params: {
                  lang: $i18n.locale,
                  slug: decodeURIComponent(material.slug)
                }}">
                  <div class="carousel--item__bg">
                    <div v-if="isVideoBackground(material)"
                        class="video-container">
                      <video :src="material.feed_data.background.background_video_url" autoplay muted playsinline loop="loop"></video>
                    </div>
                    <div v-else
                         :style="{'background-image': handleBackgroundImageForFeaturedMaterial(material)}"
                         class="img-container"></div>
                  </div>
                  <div class="carousel--item__content">
                    <div class="categories" :style="{'background-color': handleBackgroundColorForProductCategory(material)}">
                      <p>
                        <span class="bold" v-html="material.categories.product_category.name"></span>&nbsp;
                        {{material.categories.material_type.name}}
                      </p>
                    </div>
                    <div class="title">
                      <h3>
                        <template v-if="material.feed_data && material.feed_data.heading">
                          <span class="bold">{{material.feed_data.heading}}</span>
                          <span v-if="material.feed_data.subheading"><br>{{material.feed_data.subheading}}</span>
                        </template>
                        <span v-else class="bold">{{material.post_title}}</span>
                      </h3>
                    </div>
                    <div class="data-bottom">
                      <div v-if="material.stats && (material.stats.average_rating || material.stats.page_visits)" class="stats">
                        <div v-if="material.stats.average_rating" class="rating">
                          <img src="@/assets/icons/rating.svg"/>
                          <span class="data">{{material.stats.average_rating}}</span>
                          <span class="desc" v-html="$t('feed.averageRating')"></span>
                        </div>
                        <div v-if="material.comment_count > 0" class="comments">
                          <img src="@/assets/icons/comments.svg"/>
                          <span class="data">{{material.comment_count}}</span>
                          <span class="desc" v-html="$t('feed.articleComments')"></span>
                        </div>
                        <div v-if="material.stats.page_visits" class="views">
                          <img src="@/assets/icons/person-read.svg"/>
                          <span class="data">{{material.stats.page_visits}}</span>
                          <span class="desc" v-html="$t('feed.timesRead')"></span>
                        </div>
                      </div>
                      <div v-if="material.comment_competition_enabled" class="competition-icon-info">
                        <img class="img-fluid icon-trophy filter-to-white"
                             src="@/assets/icons/trophy.svg"/>
                        <span class="competition-icon-info--text">{{$t('contest.contestArticle')}}</span>
                      </div>
                    </div>
                  </div>
                </localized-link>
              </div>
            </VueSlickCarousel>
          </div>
        </div>
      </div>
      <div class="col-lg-3">
        <template v-if="recentCommentCompetitionMaterials">
          <div class="contest-box">
            <div class="bg-gradient-light-grey-vertical py-3 px-4">
              <h2 class="text-xs text-secondary semi-bold font-italic-custom m-0">{{$t('feed.contest')}}</h2>
            </div>
            <div class="row">
              <div class="col-sm-6 col-lg-12">
                <div class="contest-box--banner">
                  <localized-link :to="{name: 'About'}">
                    <img src="@/assets/contest_banner.jpg" class="img-fluid"/>
                    <div class="text-overlay">
                      <h3 class="py-3 px-4" v-html="$t('feed.contestBannerText')"></h3>
                    </div>
                  </localized-link>
                </div>
              </div>
              <div class="col-sm-6 col-lg-12">
                <div class="contest-box--recent-articles bg-gradient-light-grey-vertical py-3 px-4">
                  <h3 class="text-xs text-secondary semi-bold font-italic-custom">{{$t('feed.allContestArticles')}}</h3>
                  <ul>
                    <li v-for="material in recentCommentCompetitionMaterials" :key="material.id">
                      <localized-link :to="{name: 'Material', params: {
                        lang: $i18n.locale,
                        slug: decodeURIComponent(material.slug)
                      }}">
                        {{material.post_title}}
                      </localized-link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="recently-commented-box">
            <div class="row">
              <div class="col-sm-6 col-lg-12">
                <div class="recently-commented-box--banner">
                  <localized-link :to="{name: 'About'}">
                    <img src="@/assets/contest_banner.jpg" class="img-fluid"/>
                    <div class="text-overlay">
                      <h3 class="py-3 px-4 text-sm semi-bold font-italic">"{{$t('aboutPage.quote')}}"</h3>
                    </div>
                  </localized-link>
                </div>
              </div>
              <div v-if="recentlyCommentedMaterials" class="col-sm-6 col-lg-12">
                <div class="contest-box--recent-articles bg-gradient-light-grey-vertical py-3 px-4">
                  <h3 class="text-xs text-secondary semi-bold font-italic-custom text-uppercase">
                    {{$t('feed.recentlyCommentedArticles')}}
                  </h3>
                  <ul>
                    <li v-for="material in recentlyCommentedMaterials"
                        :key="`recently-commented-${material.ID}`">
                      <localized-link :to="{name: 'Material', params: {
                        lang: $i18n.locale,
                        slug: decodeURIComponent(material.slug)
                      }}">
                        {{material.post_title}}
                      </localized-link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="row justify-content-center mb-4">
      <div v-if="topRatedMaterials" class="col-lg-6 col-xl-4">
        <div class="bg-gradient-light-grey-vertical">
          <h2 class="text-xs text-secondary semi-bold font-italic-custom py-3 px-4 m-0">{{$t('feed.topRated')}}</h2>
        </div>
        <CarouselSmall :materials="topRatedMaterials" dataType="rating"/>
      </div>
      <div v-if="mostCommentedMaterials" class="col-lg-6 col-xl-4">
        <div class="bg-gradient-light-grey-vertical">
          <h2 class="text-xs text-secondary semi-bold font-italic-custom py-3 px-4 m-0">{{$t('feed.mostCommented')}}</h2>
        </div>
        <CarouselSmall :materials="mostCommentedMaterials" dataType="comments"/>
      </div>
      <div v-if="mostVisitedMaterials" class="col-lg-6 col-xl-4">
        <div class="bg-gradient-light-grey-vertical">
          <h2 class="text-xs text-secondary semi-bold font-italic-custom py-3 px-4 m-0">{{$t('feed.mostRead')}}</h2>
        </div>
        <CarouselSmall :materials="mostVisitedMaterials" dataType="visits"/>
      </div>
    </div>
    <div v-if="latestMaterials" class="row">
      <div class="col">
        <div class="bg-gradient-light-grey-vertical">
          <h2 class="text-xs text-secondary semi-bold font-italic-custom py-3 px-4 m-0">{{$t('feed.newStories')}}</h2>
        </div>
        <div v-for="material in latestMaterials" :key="material.id" class="mb-2">
          <localized-link :to="{name: 'Material', params: {
            lang: $i18n.locale,
            slug: decodeURIComponent(material.slug)
          }}">
            <div class="latest-material-item" :class="{'comment-competition-enabled': material.comment_competition_enabled}">
              <div class="material-data">
                <div class="featured-image mr-1 mr-md-2 mr-lg-5"
                      :style="{'background-image': `url(${material.featured_image})`}"></div>
                <div class="py-3 px-3 d-flex flex-column">
                  <div class="categories text-uppercase text-secondary text-sm mb-1 mb-md-2">
                    <span v-if="material.material_purpose.name">{{material.material_purpose.name}} | </span>
                    <span v-if="material.categories && material.categories.material_type">
                      {{material.categories.material_type.name}} | </span>
                    <span v-if="material.content_type.name">{{material.content_type.name}}</span>
                  </div>
                  <h3 class="text-lg bold">
                    {{material.post_title}}
                  </h3>
                  <p v-if="material.feed_data && material.feed_data.subheading" class="latest-material-item__subtitle">
                    {{ material.feed_data.subheading }}
                  </p>
                  <div class="material-applications" v-if="material.applications">
                    <div v-for="application in material.applications" :key="application.text"
                        class="d-flex justify-content-start align-items-center">
                      <img :src="application.icon" class="mr-3">
                      <p v-html="application.text" class="m-0"></p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="additional-info">
                <div v-if="material.categories && material.categories.product_category"
                     class="product-category"
                     :style="{'background-color': handleBackgroundColorForProductCategory(material)}">
                  <span class="font-italic-custom text-uppercase d-inline-block bold text-sm"
                        v-html="material.categories.product_category.name"></span>
                </div>
                <div v-if="material.comment_competition_enabled" class="competition-icon-info">
                  <img class="img-fluid icon-trophy filter-to-white"
                       src="@/assets/icons/trophy.svg"/>
                  <span class="competition-icon-info--text">{{$t('contest.contestArticle')}}</span>
                </div>
              </div>
            </div>
          </localized-link>
        </div>
        <div v-if="latestMaterialsLoadingMore" class="text-center py-5">
          <b-spinner type="grow" variant="secondary"></b-spinner>
        </div>
        <div v-view="handleLoadingMoreRelatedMaterials" class="py-1"></div>
      </div>
    </div>
  </div>
</template>
<script>
import CarouselSmall from '@/components/CarouselSmall.vue';
import SearchForm from '@/components/SearchForm.vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import VueSlickCarousel from 'vue-slick-carousel';
import 'vue-slick-carousel/dist/vue-slick-carousel.css';
import 'vue-slick-carousel/dist/vue-slick-carousel-theme.css';

export default {
  metaInfo() {
    return {
      title: this.$t('feed.metaTitle'),
      meta: [
        {
          name: 'description',
          content: this.$t('feed.metaDesc'),
        },
      ],
    };
  },
  data() {
    return {
      popularPhrasesEN: [
        {
          name: 'condenser',
          isHighlighted: false,
        },
        {
          name: 'learning',
          isHighlighted: false,
        },
        {
          name: 'compressor',
          isHighlighted: false,
        },
        {
          name: 'turbo',
          isHighlighted: false,
        },
        {
          name: 'flushing',
          isHighlighted: false,
        },
        {
          name: 'webinar',
          isHighlighted: true,
        },
      ],
      popularPhrasesPL: [
        'skraplacz',
        'szkolenia',
        'sprężarka',
        'turbo',
        'płukanie',
        'EGR',
      ],
      popularPhrasesFR: [
        'CONDENSEUR',
        "COURS D'AUTO",
        'Compresseur',
        'TURBO',
        'Rinçage',
        'Suie',
      ],
      popularPhrasesIT: [
        'Condensatore',
        'RISOLUZIONE DEI PROBLEMI',
        'COMPRESSORE',
        'TURBO',
        'Lavaggio',
        'Fuliggine',
      ],
    };
  },
  components: {
    VueSlickCarousel,
    CarouselSmall,
    SearchForm,
  },
  computed: {
    ...mapGetters({
      featuredMaterials: 'feed/featuredMaterials',
      topRatedMaterials: 'feed/topRatedMaterials',
      mostVisitedMaterials: 'feed/mostVisitedMaterials',
      mostCommentedMaterials: 'feed/mostCommentedMaterials',
      latestMaterials: 'feed/latestMaterials',
      latestMaterialsLoadingMore: 'feed/latestMaterialsLoadingMore',
      latestMaterialsReachedTheEnd: 'feed/latestMaterialsReachedTheEnd',
      recentCommentCompetitionMaterials: 'feed/recentCommentCompetitionMaterials',
      recentlyCommentedMaterials: 'feed/recentlyCommentedMaterials',
    }),
  },
  mounted() {
    this.$store.dispatch('feed/GET_FEATURED_MATERIALS');
    this.$store.dispatch('feed/GET_TOP_RATED_MATERIALS');
    this.$store.dispatch('feed/GET_MOST_VISITED_MATERIALS');
    this.$store.dispatch('feed/GET_MOST_COMMENTED_MATERIALS');
    this.getLatestMaterials();
    this.$store.dispatch('feed/GET_RECENT_COMMENT_COMPETITION_MATERIALS');
    this.$store.dispatch('feed/GET_RECENTLY_COMMENTED_MATERIALS');
  },
  methods: {
    getLatestMaterials(loadingMore = false) {
      this.$store.dispatch('feed/GET_LATEST_MATERIALS', { loadingMore });
    },
    handleLoadingMoreRelatedMaterials(e) {
      if (e.type === 'enter' && e.scrollValue && !this.latestMaterialsReachedTheEnd && this.latestMaterials) {
        this.debounceLoadingMoreRelatedMaterials();
      }
    },
    // eslint-disable-next-line
    debounceLoadingMoreRelatedMaterials: _.debounce(function() {
      this.getLatestMaterials(true);
    }, 100),
    handleBackgroundImageForFeaturedMaterial(material) {
      let imageProperty = '';
      if (
        material.feed_data?.background?.background_type === 'Image'
        && material.feed_data?.background?.featured_image?.sizes
      ) {
        imageProperty = `url(${material.feed_data.background.featured_image.sizes.large})`;
      }
      if (
        !material.feed_data?.background?.featured_image?.sizes
        && material.featured_image
      ) {
        imageProperty = `url(${material.featured_image})`;
      }
      return imageProperty;
    },
    isVideoBackground(material) {
      return material.feed_data?.background?.background_type === 'Video' && material.feed_data?.background?.background_video_url;
    },
    handleBackgroundColorForProductCategory(material) {
      let bgColor = '';
      if (material.categories?.product_category?.highlight_color) {
        bgColor = `${material.categories.product_category.highlight_color}cc`;
      } else {
        bgColor = '#00adefcc';
      }
      return bgColor;
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/scss/common.scss';
.search-box {
  > div {
    width: 100%;
  }
  p {
    flex-shrink: 3;
    line-height: 1.2em;
  }
  &::v-deep .header-search {
    width: 100%!important;
  }
}
.popular-phrases {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  column-gap: 3px;
  row-gap: 3px;
  .phrase {
    background-color: $light-grey;
    text-transform: uppercase;
    width: 100%;
    transition: background .3s ease-in-out;
    &.highlighted {
      background-color: $primary;
      color: white;
      a {
        color: white;
      }
    }
    &:hover {
      background-color: $primary;
      color: white;
    }
    a {
      display: flex;
      justify-content: center;
      align-items: center;
      text-align: center;
      padding: 0 5px;
      height: 100%;
    }
  }
}
.side-btn {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  color: white;
  padding: 0 1rem;
  line-height: 1.5;
  font-size: 14px;
  @extend .text-sm;
  &.oil-gas-finder-btn {
    background-color: $secondary;
    margin-bottom: 3px;
  }
  &.catalogue-btn {
    background-color: $tertiary;
  }
  img {
    max-width: 50px;
    min-width: 44px;
    max-height: 18px;
    margin-right: 10px;
  }
}
.carousel {
  &--item {
    position: relative;
    &__bg {
      position: absolute;
      top: 0; left: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
      .video-container {
        width: 100%;
        height: 100%;
      }
      .img-container {
        width: 100%;
        height: 100%;
        background-size: cover;
        background-position: center center;
      }
      video {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    &__content {
      min-height: 500px;
      position: relative;
      .categories {
        position: absolute;
        left: 0; top: 0;
        min-width: 33%;
        color: white;
        padding: 1rem 2rem;
        p {
          @extend .font-italic-custom;
          font-size: 18px;
          margin: 0;
          text-transform: uppercase;
        }
      }
      .title {
        @extend .bg-primary-opacity-80;
        position: absolute;
        right: 0; top: 0;
        width: 33%;
        height: 100%;
        padding: 3rem 2.5rem 3rem 2rem;
        h3 {
          word-break: break-word;
          margin-bottom: 0;
          color: white;
        }
      }
      .data-bottom {
        position: absolute;
        left: 0; bottom: 0;
        width: 67%;
        display: flex;
      }
      .stats {
        @extend .bg-primary-opacity-80;
        display: flex;
        justify-content: space-around;
        padding: 0.5rem 1rem;
        width: -webkit-fill-available;
        > div {
          display: flex;
          align-items: center;
          color: white;
          &:not(:last-child) {
            margin-right: 1rem;
          }
          img {
            max-width: 40px;
            margin-right: .7rem;
          }
          .data {
            @extend .font-italic-custom;
            font-weight: bold;
            font-size: 1.55rem;
            margin-right: .5rem;
          }
          .desc {
            font-size: .7rem;
            line-height: 1.1em;
          }
        }
      }
      .competition-icon-info {
        display: flex;
        align-items: center;
        background: #b1332796;
        padding: 5px 10px;
        color: white;
        &--text {
          font-size: .7rem;
          line-height: 1.1em;
          display: inline-block;
          width: min-content;
          margin-left: 8px;
        }
        .icon-trophy {
          max-width: 26px;
          width: 26px;
        }
      }
    }
  }
  @include media-breakpoint-down(lg) {
    &--item {
      &__content {
        h3 {
          font-size: 20px;
        }
      }
    }
  }
  @include media-breakpoint-down(md) {
    &--item {
      &__content {
        .categories {
          padding: .8rem 1.6rem;
          p {
            font-size: 14px;
          }
        }
        .stats, .competition-icon-info {
          img {
            max-width: 30px!important;
            width: 100%;
          }
          .data {
            font-size: 1.2rem!important;
          }
          .desc, &--text {
            font-size: .6rem!important;
          }
        }
      }
    }
  }
  @include media-breakpoint-down(sm) {
    &--item {
      &__content {
        .title {
          left: 0; bottom: 0; top: unset;
          min-height: 30%;
          height: unset;
          width: 50%;
          padding: 1rem 1.5rem;
          display: flex;
          align-items: center;
          h3 {
            width: 100%;
            font-size: 18px;
          }
        }
        .data-bottom {
          right: 0; left: unset;
          width: 50%;
          flex-direction: column;
        }
        .stats {
          flex-direction: column;
          > div:not(:last-child) {
            margin-bottom: .5rem;
          }
          > div {
            width: max-content;
          }
          img {
            max-width: 20px!important;
          }
        }
      }
    }
  }
}
::v-deep .slick-arrow {
  @extend .bg-primary-lighter-opacity-80;
  position: absolute;
  z-index: 2;
  width: 40px;
  height: 90px;
  &.slick-prev {
    left: 0;
    &::before {
      transform: rotate(180deg);
    }
  }
  &.slick-next {
    right: 0;
  }
  &::before {
    content: '';
    position: absolute;
    width: 50%;
    height: 70%;
    margin: auto;
    top: 0; left: 0; bottom: 0; right: 0;
    background-image: url('../assets/icons/arrow-thin.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center center;
  }
}

.latest-material-item {
  @extend .bg-gradient-light-grey-horizontal;
  display: flex;
  justify-content: space-between;
  &.comment-competition-enabled {
    @extend .bg-gradient-light-tertiary-horizontal;
  }
  .latest-material-item__subtitle {
    font-size: 18px;
    color: $primary;
    font-weight: 100;
  }
  .featured-image {
    width: 230px;
    height: 230px;
    min-width: 230px;
    max-width: 230px;
    background-size: cover;
    background-position: center center;
  }
  .material-data {
    display: flex;
    .material-applications {
      margin-top: auto;
      width: 100%;
      font-size: 15px;
      > div {
        &:first-child p {
          font-weight: bold;
        }
        &:not(:last-of-type) {
          margin-bottom: .25em;
        }
      }
      img {
        max-width: 45px;
        width: 100%;
        filter: $filter-to-lightgrey;
      }
    }
  }
  .additional-info {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .product-category {
    background: $secondary;
    height: fit-content;
    color: white;
    padding: 1rem 2.5rem;
    span {
      white-space: nowrap;
    }
  }
  .competition-icon-info {
    display: flex;
    align-items: center;
    justify-content: center;
    background: $tertiary;
    padding: 10px 10px;
    color: white;
    &--text {
      font-size: .7rem;
      line-height: 1.1em;
      display: inline-block;
      width: min-content;
      margin-left: 8px;
    }
    .icon-trophy {
      max-width: 26px;
      width: 26px;
    }
  }
  @include media-breakpoint-down(lg) {
    .material-applications {
      font-size: 12px!important;
    }
  }
  @include media-breakpoint-down(md) {
    .featured-image {
      width: 180px;
      min-width: 180px;
      max-width: 180px;
      height: 180px;
    }
    .product-category {
      padding: .6rem 2rem;
    }
  }
  @include media-breakpoint-down(sm) {
    position: relative;
    .featured-image {
      width: 120px;
      min-width: 120px;
      max-width: 120px;
      min-height: 155px;
      height: 100%;
    }
    .material-data > div {
      padding-top: 39px!important;
    }
    .additional-info {
      position: absolute;
      right: 0;
      flex-direction: row;
      align-items: unset;
      .product-category {
        height: auto;
        padding: .3rem 1.5rem;
        line-height: 1em;
        display: flex;
        align-items: center;
        span {
          font-size: 10px!important;
        }
      }
      .competition-icon-info {
        padding: 5px 10px;
        &--text {
          font-size: 10px;
        }
        .icon-trophy {
          max-width: 15px;
        }
      }
    }
    .categories {
      font-size: 8px!important;
    }
    h3 {
      font-size: 16px;
    }
  }
}
.contest-box {
  &--banner {
    position: relative;
    img {
      width: 100%;
    }
    .text-overlay {
      position: absolute;
      top: 0;
      left: 0;
      color: white;
      h3 {
        font-size: 22px;
      }
    }
  }
  &--recent-articles {
    h3 {
      margin-bottom: 1rem;
    }
    ul {
      list-style: none;
      padding: 0;
      li {
        @extend .text-xs;
        margin-bottom: .5rem;
        a {
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
}
.recently-commented-box--banner {
  position: relative;
  img {
    width: 100%;
  }
  .text-overlay {
    position: absolute;
    top: 0;
    left: 0;
    color: white;
  }
}
</style>
